/* Section: cart_drawer — "Your Cart"

   Owns three things:

     1. the panel itself (open/close, scrim, Escape, focus trap)
     2. every cart mutation, as an AJAX call — quantity, remove, variant
        swap, discount code, and the recommendation rail's "+" buttons
     3. the re-render: after any mutation the section is fetched again
        through the Section Rendering API and only [data-cart-drawer-body]
        is swapped, so Liquid stays the single source of truth for the
        markup and the panel keeps its open state and scroll position.

   The markup is re-created on every refresh, so nothing inside the body is
   bound directly: all handlers are delegated from the root element, which
   survives.

   Add to cart: with the section's `open_on_add` setting on, Munchief's
   product forms (a real <form action="/cart/add">) are intercepted and
   posted to /cart/add.js instead, then the drawer opens. With it off the
   forms submit normally and the visitor lands on /cart — the pre-drawer
   behaviour, and the fallback whenever this script fails to load. */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* Shopify.routes.root carries the locale prefix (e.g. "/en-in/"), so every
     AJAX endpoint is built off it rather than hardcoded at the domain root. */
  function cartUrl(endpoint) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'cart/' + endpoint + '.js';
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) throw new Error(data.description || data.message || response.statusText);
        return data;
      });
    });
  }

  function init(root) {
    if (root.dataset.cartDrawerReady === 'true') return;
    root.dataset.cartDrawerReady = 'true';

    var panel = root.querySelector('.cart-drawer__panel');
    var sectionId = root.dataset.sectionId || 'munchief-cart-drawer';
    var lastFocused = null;
    var busy = false;

    function body() {
      return root.querySelector('[data-cart-drawer-body]');
    }

    function lockScroll(locked) {
      document.documentElement.style.overflow = locked ? 'hidden' : '';
      document.body.style.overflow = locked ? 'hidden' : '';
    }

    function open() {
      if (root.classList.contains('is-open')) return;

      lastFocused = document.activeElement;
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      lockScroll(true);

      var target = root.querySelector('.cart-drawer__close, .cart-drawer__back');
      if (target) target.focus({ preventScroll: true });
    }

    function close() {
      if (!root.classList.contains('is-open')) return;

      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      lockScroll(false);

      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
    }

    function trapFocus(event) {
      var items = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (element) {
        return element.offsetParent !== null && element.tabIndex >= 0;
      });
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* Header bubbles live outside this section, so they are updated by hand
       rather than re-rendered. */
    function syncCount(count) {
      root.dataset.cartCount = String(count);

      var bubbles = document.querySelectorAll('.site-header__cart-count');
      Array.prototype.forEach.call(bubbles, function (bubble) {
        bubble.textContent = count;
        bubble.dataset.count = String(count);
      });
    }

    function refresh() {
      var url = window.location.pathname + '?sections=' + encodeURIComponent(sectionId);

      return fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          var html = data[sectionId];
          if (!html) return;

          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-cart-drawer-body]');
          var current = body();
          if (!fresh || !current) return;

          current.innerHTML = fresh.innerHTML;

          var freshRoot = parsed.querySelector('[data-munchief-cart-drawer]');
          if (freshRoot) syncCount(parseInt(freshRoot.dataset.cartCount, 10) || 0);
        });
    }

    /* One in-flight mutation at a time: the row is disabled while it runs, so
       a double-click cannot queue two conflicting line numbers. */
    function mutate(work, scope) {
      if (busy) return Promise.resolve();
      busy = true;
      if (scope) scope.classList.add('is-busy');

      return work()
        .then(refresh)
        .catch(function (error) {
          showCouponError(error.message);
        })
        .then(function () {
          busy = false;
          if (scope && scope.isConnected) scope.classList.remove('is-busy');
        });
    }

    function changeLine(line, quantity, scope) {
      return mutate(function () {
        return postJSON(cartUrl('change'), { line: line, quantity: quantity });
      }, scope);
    }

    function addVariant(id, quantity) {
      return mutate(function () {
        return postJSON(cartUrl('add'), { items: [{ id: id, quantity: quantity || 1 }] });
      });
    }

    /* A variant swap is a remove plus an add: /cart/change.js can move a line
       but cannot repoint it at a different variant. */
    function swapVariant(line, variantId, quantity, scope) {
      return mutate(function () {
        return postJSON(cartUrl('change'), { line: line, quantity: 0 }).then(function () {
          return postJSON(cartUrl('add'), { items: [{ id: variantId, quantity: quantity }] });
        });
      }, scope);
    }

    function showCouponError(message) {
      var target = root.querySelector('[data-cart-drawer-coupon-error]');
      if (!target) return;

      if (message) {
        target.textContent = message;
        target.hidden = false;
      } else {
        target.textContent = '';
        target.hidden = true;
      }
    }

    function appliedCodes() {
      var buttons = root.querySelectorAll('[data-cart-drawer-coupon-remove]');
      return Array.prototype.map.call(buttons, function (button) {
        return button.dataset.cartDrawerCouponRemove;
      });
    }

    /* /cart/update.js takes the whole comma-separated list, so applying and
       removing are the same call with a different list. It answers 200 for a
       code that does not exist, flagging it applicable:false instead. */
    function setDiscounts(codes, checkFor) {
      return mutate(function () {
        return postJSON(cartUrl('update'), { discount: codes.join(',') }).then(
          function (data) {
            if (!checkFor) return data;

            var rejected = (data.discount_codes || []).some(function (discount) {
              return discount.code === checkFor && discount.applicable === false;
            });
            if (rejected) throw new Error('That code is not valid for this cart.');
            return data;
          }
        );
      });
    }

    function applyCoupon() {
      var input = root.querySelector('[data-cart-drawer-coupon-input]');
      if (!input) return;

      var code = input.value.trim();
      showCouponError('');
      if (!code) return;

      var codes = appliedCodes();
      if (codes.indexOf(code) === -1) codes.push(code);

      setDiscounts(codes, code);
    }

    function lineOf(element) {
      var item = element.closest('[data-cart-drawer-item]');
      return item ? { item: item, line: parseInt(item.dataset.line, 10) } : null;
    }

    root.addEventListener('click', function (event) {
      var target = event.target;

      if (target.closest('[data-cart-drawer-close]')) {
        close();
        return;
      }

      /* Empties the whole cart in one call; refresh() then swaps in the
         drawer's empty state. */
      if (target.closest('[data-cart-drawer-clear]')) {
        mutate(function () {
          return postJSON(cartUrl('clear'), {});
        }, panel.querySelector('[data-cart-drawer-items]'));
        return;
      }

      var quantityButton = target.closest('[data-cart-drawer-qty]');
      if (quantityButton) {
        var context = lineOf(quantityButton);
        if (context) {
          changeLine(context.line, parseInt(quantityButton.dataset.cartDrawerQty, 10), context.item);
        }
        return;
      }

      var couponSubmit = target.closest('[data-cart-drawer-coupon-submit]');
      if (couponSubmit) {
        applyCoupon();
        return;
      }

      var couponRemove = target.closest('[data-cart-drawer-coupon-remove]');
      if (couponRemove) {
        var code = couponRemove.dataset.cartDrawerCouponRemove;
        showCouponError('');
        setDiscounts(
          appliedCodes().filter(function (existing) {
            return existing !== code;
          })
        );
        return;
      }

      var recAdd = target.closest('[data-cart-drawer-rec-add]');
      if (recAdd) {
        addVariant(parseInt(recAdd.dataset.cartDrawerRecAdd, 10), 1);
        return;
      }

      var recPrev = target.closest('[data-cart-drawer-recs-prev]');
      var recNext = target.closest('[data-cart-drawer-recs-next]');
      if (recPrev || recNext) {
        var track = root.querySelector('[data-cart-drawer-recs-track]');
        if (track) {
          var card = track.querySelector('.cart-drawer__rec');
          var step = card ? card.offsetWidth + 8 : track.clientWidth;
          track.scrollBy({ left: recNext ? step : -step, behavior: 'smooth' });
        }
      }
    });

    root.addEventListener('change', function (event) {
      var select = event.target.closest('[data-cart-drawer-variant]');
      if (!select) return;

      var context = lineOf(select);
      if (!context) return;

      var quantityNode = context.item.querySelector('.qty-stepper__value');
      var quantity = quantityNode ? parseInt(quantityNode.textContent, 10) || 1 : 1;

      swapVariant(context.line, parseInt(select.value, 10), quantity, context.item);
    });

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      if (!event.target.closest('[data-cart-drawer-coupon-input]')) return;

      event.preventDefault();
      applyCoupon();
    });

    document.addEventListener('keydown', function (event) {
      if (!root.classList.contains('is-open')) return;
      if (event.key === 'Escape') close();
      if (event.key === 'Tab') trapFocus(event);
    });

    /* Anything on the page can open the drawer: the header's cart button
       carries data-munchief-cart-open, and other scripts can dispatch
       `munchief:cart:open` (bundle_builder does, after its one add call). */
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-munchief-cart-open]');
      if (!trigger) return;

      event.preventDefault();
      open();
    });

    document.addEventListener('munchief:cart:open', function () {
      refresh().then(open);
    });

    document.addEventListener('munchief:cart:refresh', function () {
      refresh();
    });

    if (root.dataset.openOnAdd === 'true') {
      document.addEventListener('submit', function (event) {
        var form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!form.closest('.munchief')) return;
        if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;

        event.preventDefault();

        var submitButton = form.querySelector('[name="add"]');
        if (submitButton) submitButton.classList.add('is-busy');

        function done() {
          if (submitButton) submitButton.classList.remove('is-busy');
        }

        fetch(cartUrl('add'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        })
          .then(function (response) {
            return response.json().then(function (payload) {
              if (!response.ok) throw new Error(payload.description || response.statusText);
              return payload;
            });
          })
          .then(
            function () {
              /* The line is already in the cart. Anything that fails from
                 here is a render problem, never a reason to re-POST — that
                 would add the item a second time. */
              return refresh().then(open, open).then(done);
            },
            function () {
              /* The add itself failed (sold out, AJAX cart unavailable).
                 Let the plain POST through so the visitor gets a real
                 error page rather than a silent no-op. */
              done();
              form.submit();
            }
          );
      });
    }

    root.munchiefCartDrawer = { open: open, close: close, refresh: refresh };
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-munchief-cart-drawer]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
